/**
 * Workspace session API — create sessions, send messages, inspect workspace.
 */
import { apiClient, buildUrl } from './apiClient';

// ── Types ────────────────────────────────────────────────────────────

export interface WorkspaceSession {
  session_id: string;
  project_id: string;
  tenant_id: string;
  user_id: string;
  batch_id?: string | null;
  attachment_point: string;
  status: string;
  workspace_meta?: Record<string, any> | null;
  agent_busy?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface WorkspaceMessage {
  message_id: string;
  session_id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content?: string | null;
  tool_calls?: any[] | null;
  tool_name?: string | null;
  metadata: Record<string, any>;
  created_at?: string;
}

export interface WorkspaceMessageResult {
  content: string;
  operations: WorkspaceOperation[];
  workspace_changes: string[];
  token_usage: Record<string, number>;
}

export interface WorkspaceOperation {
  operation_id: string;
  session_id: string;
  operation_type: string;
  label?: string | null;
  description?: string | null;
  status: string;
  output_ref?: string | null;
  input_refs: string[];
  config: Record<string, any>;
  row_count_before?: number | null;
  row_count_after?: number | null;
  error?: string | null;
  review_result?: {
    passed: boolean;
    checks: Array<{ name: string; passed: boolean; measured?: number; threshold?: number }>;
    corrections: Array<{ check_name: string; fix_type: string; description: string; succeeded: boolean }>;
    warnings: Array<{ check_name: string; severity: string; message: string }>;
  } | null;
  review_flags: string[];
  exploratory?: boolean;
  config_history?: Array<{
    config: Record<string, any>;
    row_count_after?: number;
    output_schema?: Record<string, string>;
    edited_by: string;
    edited_at: string;
    reason: string;
  }>;
  created_at?: string;
}

export interface WorkspaceStreamEvent {
  type:
    | 'tool_start' | 'tool_result'
    | 'review_start' | 'review_result'
    | 'thinking' | 'message_complete' | 'workspace_change'
    | 'pipeline_start' | 'step_start' | 'step_complete'
    | 'pipeline_complete' | 'pipeline_failed' | 'pipeline_paused'
    | 'operation_updated';
  session_id: string;
  data: Record<string, any>;
  timestamp: string;
}

export interface WorkspaceTable {
  name: string;
  row_count: number;
  column_count: number;
  layer: string;
  origin_ref: string;
  columns: Array<{ name: string; type: string }>;
  // Registry-enriched fields (from table_identity_registry)
  display_name?: string | null;
  source_type?: string | null;   // file_upload | connector | transform | mapped_view
  connector_label?: string | null;
}

export interface WorkspaceStatus {
  ready: boolean;
  session_id: string;
}

export interface CreateSessionParams {
  project_id: string;
  tenant_id: string;
  user_id: string;
  batch_id?: string;
  attachment_point?: string;
}

// ── API Functions ────────────────────────────────────────────────────

export async function createWorkspaceSession(params: CreateSessionParams): Promise<WorkspaceSession> {
  const response = await apiClient.post(
    buildUrl('/api/v1/agents/sessions'),
    params,
  );
  return response.data;
}

export async function getWorkspaceSession(sessionId: string): Promise<WorkspaceSession> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/agents/sessions/${sessionId}`),
  );
  return response.data;
}

/**
 * Get the most recent active session for a batch.
 * Returns null (not an error) when no active session exists.
 */
export async function getActiveSessionForBatch(batchId: string): Promise<WorkspaceSession | null> {
  try {
    const response = await apiClient.get(
      buildUrl(`/api/v1/agents/sessions/batch/${batchId}/active`),
    );
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/**
 * Non-blocking workspace readiness check.
 * Returns {ready: true} when the data-worker workspace is live.
 * Triggers background reconstruction if not ready.
 */
export async function getWorkspaceStatus(sessionId: string, signal?: AbortSignal): Promise<WorkspaceStatus> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace/status`),
    { signal, timeout: 10_000 },
  );
  return response.data;
}

export async function sendWorkspaceMessage(
  sessionId: string,
  message: string,
  displayText?: string,
): Promise<WorkspaceMessageResult> {
  // Message turns can take 2+ minutes for large datasets (multi-turn LLM
  // tool loops against millions of rows). 5 minutes allows for the full
  // 15-turn budget without premature timeout. SSE events stream real-time
  // progress during this wait.
  const body: Record<string, string> = { message };
  if (displayText) body.display_text = displayText;
  const response = await apiClient.post(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/messages`),
    body,
    { timeout: 300000 },
  );
  return response.data;
}

export async function getWorkspaceMessages(sessionId: string): Promise<WorkspaceMessage[]> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/messages`),
  );
  return response.data;
}

export async function getWorkspaceOperations(sessionId: string): Promise<WorkspaceOperation[]> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/operations`),
  );
  return response.data;
}

export async function getWorkspaceTables(sessionId: string): Promise<WorkspaceTable[]> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace`),
  );
  return response.data;
}

export interface PersistedPipelineStep {
  step_id: string;
  step_type: string;
  title: string;
  status: string;
  step_order: number;
  input_refs: string[];
  output_ref?: string | null;
  row_count?: number | null;
  metrics?: Record<string, any>;
  error?: string | null;
  source_operation_id?: string | null;
}

export async function getSessionPipelineSteps(
  sessionId: string,
): Promise<{ pipeline_id: string | null; steps: PersistedPipelineStep[] }> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/pipeline/steps`),
  );
  return response.data;
}

export async function promoteOperation(
  sessionId: string,
  operationId: string,
): Promise<{ status: string; operation_id: string }> {
  const response = await apiClient.patch(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/operations/${operationId}/promote`),
  );
  return response.data;
}

export async function discardOperation(
  sessionId: string,
  operationId: string,
): Promise<{ status: string; operation_id: string }> {
  const response = await apiClient.patch(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/operations/${operationId}/discard`),
  );
  return response.data;
}

export interface TablePreview {
  table_name: string;
  row_count: number;
  columns: string[];
  rows: Record<string, any>[];
}

export async function previewWorkspaceTable(
  sessionId: string,
  tableName: string,
  limit = 20,
): Promise<TablePreview> {
  const response = await apiClient.get(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace/${tableName}/preview`),
    { params: { limit } },
  );
  return response.data;
}

export async function renameOperation(
  sessionId: string,
  operationId: string,
  label: string,
): Promise<{ status: string; operation_id: string; label: string }> {
  const response = await apiClient.patch(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/operations/${operationId}/label`),
    { label },
  );
  return response.data;
}

export async function updateOperationExploratory(
  sessionId: string,
  operationId: string,
  exploratory: boolean,
): Promise<{ status: string; operation_id: string }> {
  const response = await apiClient.patch(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/operations/${operationId}/exploratory`),
    { exploratory },
  );
  return response.data;
}

export async function autoClassifyOperations(
  sessionId: string,
): Promise<{ classified_count: number }> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/operations/auto-classify`),
  );
  return response.data;
}

/** Undo Generate Mapping — delete recipe, views, canvas graph, and session mapping state. */
export async function deleteGeneratedMapping(
  sessionId: string,
): Promise<{
  status: string;
  batch_id: string;
  deleted_recipe_ids: string[];
  discarded_schema_map_ops: string[];
  dropped_views: string[];
  canvas_cleared: boolean;
  batch_status: string;
}> {
  const response = await apiClient.delete(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace/mapping`),
  );
  return response.data;
}

export async function updateOperationConfig(
  sessionId: string,
  operationId: string,
  config: Record<string, any>,
  reason?: string,
): Promise<WorkspaceOperation> {
  const response = await apiClient.patch(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/operations/${operationId}/config`),
    { config, reason: reason || 'User edited code' },
    { timeout: 120000 },
  );
  return response.data;
}

export async function closeWorkspaceSession(sessionId: string): Promise<void> {
  await apiClient.post(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/close`),
  );
}

// ── Direct Operation Creation & Session Meta ────────────────────────

export interface CreateOperationParams {
  operation_type: string;
  config: Record<string, any>;
  output_name: string;
  input_refs: string[];
  exploratory?: boolean;
}

/**
 * Create and execute a transform operation directly (bypassing agent LLM).
 * The backend executes, auto-reviews, and injects a synthetic assistant
 * message so the agent stays aware.
 */
export async function createOperation(
  sessionId: string,
  params: CreateOperationParams,
): Promise<WorkspaceOperation> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/operations`),
    params,
    { timeout: 120000 },
  );
  return response.data;
}

export interface UpdateSessionMetaResult {
  session_id: string;
  workspace_meta: Record<string, any>;
}

/**
 * Update workspace_meta fields on an agent session.
 * Used for schema selection, connector configuration, and other
 * UI-driven meta changes.
 */
export async function updateSessionMeta(
  sessionId: string,
  updates: Record<string, any>,
): Promise<UpdateSessionMetaResult> {
  const response = await apiClient.patch(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/meta`),
    { updates },
  );
  return response.data;
}

// ── Workspace Query & Column Profile ────────────────────────────────

export interface WorkspaceQueryResult {
  row_count: number;
  columns: string[];
  rows: Record<string, any>[];
  truncated: boolean;
  total_count?: number;
}

export interface WorkspaceColumnProfile {
  column_name: string;
  dtype: string;
  count: number;
  null_count: number;
  null_pct: number;
  unique_count: number;
  top_values?: Array<{ value: string; count: number }>;
  min?: string | number;
  max?: string | number;
  mean?: number;
  std?: number;
}

export async function queryWorkspace(
  sessionId: string,
  sql: string,
  limit = 200,
  offset = 0,
  includeTotalCount = false,
): Promise<WorkspaceQueryResult> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace/query`),
    { sql, limit, offset, include_total_count: includeTotalCount },
    { timeout: 60000 },
  );
  return response.data;
}

export async function previewPythonTransform(
  sessionId: string,
  code: string,
  inputTable: string,
  limit = 100,
): Promise<WorkspaceQueryResult> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace/preview-python`),
    { code, input_table: inputTable, limit },
    { timeout: 120000 },
  );
  return response.data;
}

function quoteSqlIdent(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

/** Profile a column from a query subquery (transformed / computed columns). */
export async function profileWorkspaceColumnFromSql(
  sessionId: string,
  sql: string,
  columnName: string,
  dtype = 'VARCHAR',
): Promise<WorkspaceColumnProfile> {
  const cleanSql = sql.trim().replace(/;$/, '');
  const col = quoteSqlIdent(columnName);

  const statsSql = [
    'SELECT',
    'COUNT(*) AS "__count",',
    `SUM(CASE WHEN ${col} IS NULL THEN 1 ELSE 0 END) AS "__null_count",`,
    `COUNT(DISTINCT ${col}) AS "__unique_count",`,
    `MIN(${col}::VARCHAR) AS "__min",`,
    `MAX(${col}::VARCHAR) AS "__max",`,
    `AVG(TRY_CAST(${col} AS DOUBLE)) AS "__mean",`,
    `STDDEV(TRY_CAST(${col} AS DOUBLE)) AS "__std"`,
    `FROM (${cleanSql}) AS "__prof"`,
  ].join(' ');

  const topSql = [
    `SELECT ${col}::VARCHAR AS value, COUNT(*) AS count`,
    `FROM (${cleanSql}) AS "__prof"`,
    `WHERE ${col} IS NOT NULL`,
    `GROUP BY ${col}`,
    'ORDER BY count DESC',
    'LIMIT 10',
  ].join(' ');

  const [statsResult, topResult] = await Promise.all([
    queryWorkspace(sessionId, statsSql, 1, 0, false),
    queryWorkspace(sessionId, topSql, 10, 0, false),
  ]);

  const row = statsResult.rows?.[0] ?? {};
  const count = Number(row.__count) || 0;
  const nullCount = Number(row.__null_count) || 0;

  return {
    column_name: columnName,
    dtype,
    count,
    null_count: nullCount,
    null_pct: count > 0 ? nullCount / count : 0,
    unique_count: Number(row.__unique_count) || 0,
    top_values: (topResult.rows ?? []).map((r) => ({
      value: String(r.value ?? ''),
      count: Number(r.count) || 0,
    })),
    min: row.__min,
    max: row.__max,
    mean: row.__mean != null ? Number(row.__mean) : undefined,
    std: row.__std != null ? Number(row.__std) : undefined,
  };
}

export async function profileWorkspaceColumn(
  sessionId: string,
  tableName: string,
  columnName: string,
): Promise<WorkspaceColumnProfile> {
  // Use query-param endpoint for column names with slashes (which break URL paths)
  const hasSlash = columnName.includes('/');
  const url = hasSlash
    ? buildUrl(
        `/api/v1/agents/sessions/${sessionId}/workspace/${tableName}/column-profile?column=${encodeURIComponent(columnName)}`,
      )
    : buildUrl(
        `/api/v1/agents/sessions/${sessionId}/workspace/${tableName}/columns/${encodeURIComponent(columnName)}/profile`,
      );
  const response = await apiClient.get(url);
  const raw = response.data;
  // Map backend field names (name, null_percent, distinct_count, mean_value, ...)
  // to frontend interface (column_name, null_pct, unique_count, mean, ...)
  const nullPercent: number = raw.null_percent ?? 0; // Backend: 0–100 scale
  const nullCount: number = raw.null_count ?? 0;
  const count: number = raw.row_count ?? 0;
  return {
    column_name: raw.name || raw.column_name || columnName,
    dtype: raw.dtype || 'VARCHAR',
    count,
    null_count: nullCount,
    null_pct: nullPercent / 100, // Convert 0–100 percentage to 0–1 ratio
    unique_count: raw.distinct_count ?? raw.unique_count ?? 0,
    top_values: raw.top_values,
    min: raw.min_value ?? raw.min,
    max: raw.max_value ?? raw.max,
    mean: raw.mean_value ?? raw.mean,
    std: raw.std,
  };
}

/**
 * Materialize mapping views in the workspace as __mapped_*__ DuckDB views.
 * Call after mapping generation completes or when entering mapping mode.
 */
export async function materializeMappingViews(
  sessionId: string,
  opts: { batchId?: string; recipeId?: string; recipe?: Record<string, any> },
): Promise<{
  view_names: string[];
  view_details?: Array<{ view_name: string; target_table: string; ddl: string; column_count: number }>;
  count: number;
  failed_views?: Array<{ view: string; target: string; error: string; ddl: string; error_classification?: Record<string, any> }>;
}> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace/materialize-mapping`),
    {
      batch_id: opts.batchId,
      recipe_id: opts.recipeId,
      recipe: opts.recipe,
    },
    { timeout: 30000 },
  );
  return response.data;
}

/**
 * Attempt to correct failed materialization views via LLM correction agent.
 * Returns updated results — newly created views and remaining failures.
 * Should only be called once per materialization attempt; the backend
 * persists the corrected recipe so subsequent materializations use it.
 */
export async function correctMaterialization(
  sessionId: string,
  opts: {
    batchId: string;
    failedViews: Array<{ view: string; target: string; error: string; ddl: string; error_classification?: Record<string, any> }>;
  },
): Promise<{
  status: string;
  view_names: string[];
  view_details?: Array<{ view_name: string; target_table: string; ddl: string; column_count: number }>;
  failed_views?: Array<{ view: string; target: string; error: string; ddl: string; error_classification?: Record<string, any> }>;
}> {
  const response = await apiClient.post(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace/correct-materialization`),
    {
      batch_id: opts.batchId,
      failed_views: opts.failedViews,
    },
    { timeout: 120000 },  // Correction involves LLM calls, allow longer timeout
  );
  return response.data;
}

/**
 * Download a workspace table as CSV or Parquet file.
 * Uses fetch() with credentials so HttpOnly auth cookies apply, then triggers a browser blob download.
 */
export async function downloadWorkspaceTable(
  sessionId: string,
  table: string,
  format: 'csv' | 'parquet' = 'csv',
  columns?: string[],
  sql?: string,
): Promise<void> {
  let qs = `table=${encodeURIComponent(table)}&format=${format}`;
  if (columns && columns.length > 0) {
    qs += `&columns=${encodeURIComponent(columns.join(','))}`;
  }
  if (sql) {
    qs += `&sql=${encodeURIComponent(sql)}`;
  }
  const url = buildUrl(
    `/api/v1/agents/sessions/${sessionId}/download?${qs}`,
  );

  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) {
    throw new Error(`Download failed: ${resp.status} ${resp.statusText}`);
  }

  const blob = await resp.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `${table}.${format}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

// ── Assembly output (hierarchical) ────────────────────────────────────

export interface AssembleResult {
  status: string;
  output_ref: string;
  byte_size: number;
  format: string;
  row_count: number;
  warnings: string[];
  content_type: string;
}

/**
 * Trigger on-demand hierarchical assembly of materialized mapping views.
 * Reads __mapped_*__ views, applies OutputStructure from target schema,
 * produces XML/JSON bytes stored in session storage.
 * Returns an assembly:// ref for subsequent download.
 */
export async function assembleOutput(
  sessionId: string,
  filename?: string,
): Promise<AssembleResult> {
  const body: Record<string, any> = {};
  if (filename) body.filename = filename;
  const response = await apiClient.post<AssembleResult>(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace/assemble`),
    body,
    { timeout: 300_000 },  // Assembly can take time for large datasets
  );
  return response.data;
}

/**
 * Download assembled hierarchical output (XML/JSON bytes) as a file.
 * Triggers a browser blob download.
 */
export async function downloadAssemblyOutput(
  sessionId: string,
  outputRef: string,
): Promise<void> {
  const qs = `output_ref=${encodeURIComponent(outputRef)}`;
  const url = buildUrl(
    `/api/v1/agents/sessions/${sessionId}/assembly/download?${qs}`,
  );

  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) {
    throw new Error(`Assembly download failed: ${resp.status} ${resp.statusText}`);
  }

  const blob = await resp.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  // Extract filename from output_ref (e.g. assembly://path/to/output.xml → output.xml)
  const filename = outputRef.split('/').pop() || 'output.xml';
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Fetch a truncated preview of the assembled output for in-UI display.
 */
export async function previewAssemblyOutput(
  sessionId: string,
  outputRef: string,
  maxBytes: number = 50000,
): Promise<{
  content: string;
  format: string;
  byte_size: number;
  truncated: boolean;
  output_ref: string;
}> {
  const qs = `output_ref=${encodeURIComponent(outputRef)}&max_bytes=${maxBytes}`;
  const url = buildUrl(
    `/api/v1/agents/sessions/${sessionId}/assembly/preview?${qs}`,
  );

  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) {
    throw new Error(`Assembly preview failed: ${resp.status} ${resp.statusText}`);
  }
  return resp.json();
}

// ── Pipeline versioning ─────────────────────────────────────────────

export interface ColumnDrift {
  column: string;
  drift_type: 'removed' | 'added' | 'type_changed' | 'renamed';
  old_type?: string | null;
  new_type?: string | null;
  change_class?: 'compatible' | 'breaking' | 'unknown' | null;
  suggested_rename?: string | null;
  confidence: number;
}

export interface StepDriftResult {
  step_id: string;
  status: 'green' | 'amber' | 'red';
  reason: string;
  column_drifts: ColumnDrift[];
}

export interface PipelineDriftReport {
  pipeline_id: string;
  parent_pipeline_id: string;
  results: Record<string, StepDriftResult>;
  summary: { green: number; amber: number; red: number };
  source_table_mapping: Record<string, string>;
}

export interface SourceMappingSuggestion {
  new_table: string;
  confidence: number;
  match_type: 'exact' | 'column_overlap';
}

export interface RemovedSource {
  source_id: string;
  label: string;
  status: 'removed';
}

export interface PrepareNewVersionResult {
  parent_pipeline_id: string;
  suggested_mapping: Record<string, SourceMappingSuggestion>;
  unmapped_sources: string[];
  new_tables_available: string[];
  removed_sources?: RemovedSource[];
  sources_from_removed_connectors?: string[];
  connector_groups?: Record<string, string[]>;
}

export interface NewVersionResult {
  pipeline_id: string;
  parent_pipeline_id: string;
  version: number;
  steps_cloned: number;
  drift_report: PipelineDriftReport;
  source_table_mapping: Record<string, string>;
}

export interface ReplayStepResult {
  step_id: string;
  status: string;
  row_count?: number;
  output_ref?: string | null;
  error?: string | null;
  drift_report: PipelineDriftReport | null;
}

export async function prepareNewVersion(
  sessionId: string,
  sourcePipelineId?: string,
): Promise<PrepareNewVersionResult> {
  const { data } = await apiClient.post(
    `/api/v1/agents/sessions/${sessionId}/pipeline/prepare-new-version`,
    { source_pipeline_id: sourcePipelineId },
  );
  return data;
}

export async function createNewVersion(
  sessionId: string,
  sourceTableMapping: Record<string, string>,
  sourcePipelineId?: string,
): Promise<NewVersionResult> {
  const { data } = await apiClient.post(
    `/api/v1/agents/sessions/${sessionId}/pipeline/new-version`,
    {
      source_pipeline_id: sourcePipelineId,
      source_table_mapping: sourceTableMapping,
    },
  );
  return data;
}

export async function getPipelineDrift(
  sessionId: string,
  pipelineId: string,
): Promise<PipelineDriftReport> {
  const { data } = await apiClient.get(
    `/api/v1/agents/sessions/${sessionId}/pipeline/${pipelineId}/drift`,
  );
  return data;
}

// ── Cross-batch pipeline reuse ──────────────────────────────────────

export interface ProjectPipeline {
  pipeline_id: string;
  batch_id: string;
  batch_name: string | null;
  status: string;
  version: number;
  step_count: number;
  source_tables: string[];
  created_at: string;
}

export async function listProjectPipelines(
  projectId: string,
): Promise<ProjectPipeline[]> {
  const { data } = await apiClient.get(
    buildUrl(`/api/v1/agents/pipelines/by-project/${projectId}`),
  );
  return data;
}

/**
 * Materialize a pipeline from a session's agent operations.
 * Used when a session has transforms but no explicit staging_pipelines row.
 */
export async function materializePipeline(
  sessionId: string,
): Promise<{ pipeline_id: string; [key: string]: any }> {
  const { data } = await apiClient.post(
    buildUrl(`/api/v1/agents/sessions/${sessionId}/materialize-pipeline`),
  );
  return data;
}

export interface PipelineVersion {
  pipeline_id: string;
  batch_id: string | null;
  batch_name: string | null;
  version: number;
  status: string;
  parent_pipeline_id: string | null;
  step_count: number;
  session_id: string | null;
  created_at: string;
}

export async function listBatchPipelineVersions(
  batchId: string,
): Promise<PipelineVersion[]> {
  const { data } = await apiClient.get(
    buildUrl(`/api/v1/agents/pipelines/batch/${batchId}/versions`),
  );
  return data;
}

export async function getPipelineHistory(
  pipelineId: string,
): Promise<PipelineVersion[]> {
  const { data } = await apiClient.get(
    buildUrl(`/api/v1/agents/pipelines/${pipelineId}/history`),
  );
  return data;
}

export async function replayPipelineStep(
  sessionId: string,
  pipelineId: string,
  stepId: string,
): Promise<ReplayStepResult> {
  const { data } = await apiClient.post(
    `/api/v1/agents/sessions/${sessionId}/pipeline/${pipelineId}/steps/${stepId}/replay`,
  );
  return data;
}
